import clsx from 'clsx';
import { memo } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { ExploreOutlined } from '@mui/icons-material';

import { Icon } from '../../../../../common/icon';
import Tooltip from '../../../../../common/tooltip';
import WithLink from '../../../../../common/link/withLink';
import { PORTAL_PATH } from '../../../../../routes/paths';
import { RADIUS } from '../../../../../../styles/themes';

interface StyleProps {
  isShrinked: boolean;
  isSelected: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    background: 'transparent',
    border: '1px solid',
    borderRadius: RADIUS.md,
    padding: theme.spacing(1),
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: ({ isShrinked }: StyleProps) => (isShrinked ? '10px' : theme.spacing(2)),
    marginRight: ({ isShrinked }: StyleProps) => (isShrinked ? '10px' : theme.spacing(2)),
    color: ({ isSelected }: StyleProps) =>
      isSelected ? theme.palette.primary.light : theme.palette.primary.contrastText,
    '&:hover': {
      background: theme.pageLayout.drawer.navigation.menuItemHoverBgAlt
    }
  },
  isSelected: {
    color: theme.palette.primary.light,
    background: 'transparent !important',
    cursor: 'auto'
  },
  text: {
    marginLeft: theme.spacing(1)
  },
  icon: {
    fontSize: theme.elements.icon.size.m
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  link: {
    textDecoration: 'none',
    color: 'inherit'
  },
  center: {
    justifyContent: 'center'
  }
}));

interface Props {
  isSelected: boolean;
  isShrinked: boolean;
  isMobile: boolean;
  toggleDrawerState: () => void;
}

const ProductsAndServices = ({ isSelected, isShrinked, isMobile, toggleDrawerState }: Props) => {
  const classes = useStyles({ isShrinked, isSelected });
  const textRow1 = 'Explore for more';
  const textRow2 = 'products and services';
  const icon = ExploreOutlined;

  return (
    <WithLink href={PORTAL_PATH.PRODUCTS_AND_SERVICES} className={classes.link} isSelected={isSelected}>
      <Box
        className={clsx(classes.container, isSelected && classes.isSelected, isShrinked && classes.center)}
        onClick={isMobile ? toggleDrawerState : undefined}
        data-testid="products-and-services-section-header"
      >
        <>
          {isShrinked ? (
            <Tooltip title={`${textRow1} ${textRow2}`} placement="right">
              <span className={classes.iconContainer}>
                <Icon className={clsx(classes.icon, isSelected && classes.isSelected)} icon={icon} />
              </span>
            </Tooltip>
          ) : (
            <Icon className={clsx(classes.icon, isSelected && classes.isSelected)} icon={icon} />
          )}
          {isShrinked ? null : (
            <Typography color="inherit" className={clsx(classes.text, isSelected && classes.isSelected)} variant="h3">
              <span>{textRow1}</span>
              <br />
              <span>{textRow2}</span>
            </Typography>
          )}
        </>
      </Box>
    </WithLink>
  );
};

export default memo(ProductsAndServices);
